<template>
  <div v-if="show" class="document-modal" @click="closeModal">
    <div class="document-modal-content" @click.stop>
      <div class="document-modal-header">
        <h3>{{ documentTitle }}</h3>
        <div class="document-controls">
          <!-- Image controls - only show for images -->
          <template v-if="isImage">
            <button
                class="document-control-btn"
                @click="zoomOut"
                :disabled="zoomLevel <= minZoom"
                title="Zoom Out"
            >
              <i class="fas fa-search-minus"></i>
            </button>
            <button
                class="document-control-btn"
                @click="resetZoom"
                :disabled="zoomLevel === 1"
                title="Reset Zoom"
            >
              <i class="fas fa-sync-alt"></i>
            </button>
            <button
                class="document-control-btn"
                @click="zoomIn"
                :disabled="zoomLevel >= maxZoom"
                title="Zoom In"
            >
              <i class="fas fa-search-plus"></i>
            </button>
            <button
                class="document-control-btn"
                @click="downloadDocument"
                title="Download"
            >
              <i class="fas fa-download"></i>
            </button>
          </template>

          <!-- PDF controls -->
          <template v-if="isPdf">
            <button
                class="document-control-btn"
                @click="downloadDocument"
                title="Download PDF"
            >
              <i class="fas fa-download"></i>
            </button>
          </template>

          <button class="document-close-btn" @click="closeModal" title="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="document-container" ref="documentContainer">
        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading document...</p>
        </div>

        <!-- Image Viewer -->
        <div
            v-else-if="isImage && documentDataUrl"
            class="image-viewer"
            @wheel="handleWheel"
            @mousedown="startDrag"
            @mousemove="onDrag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
        >
          <div
              class="image-wrapper"
              :style="{
                transform: `scale(${zoomLevel}) translate(${dragOffset.x}px, ${dragOffset.y}px)`,
                cursor: isDragging ? 'grabbing' : 'grab'
              }"
              ref="imageWrapper"
          >
            <img
                :src="documentDataUrl"
                :alt="documentTitle"
                ref="zoomImage"
                @load="onImageLoad"
                @error="onImageError"
            />
          </div>
        </div>

        <!-- PDF Viewer -->
        <div v-else-if="isPdf && documentDataUrl" class="pdf-viewer">
          <div class="pdf-container">
            <iframe
                :src="documentDataUrl"
                class="pdf-iframe"
                frameborder="0"
                allowfullscreen
            ></iframe>
          </div>
          <!-- Download button removed from bottom -->
        </div>

        <!-- Unsupported format -->
        <div v-else-if="!loading" class="unsupported-format">
          <i class="fas fa-exclamation-triangle"></i>
          <h4>Unable to Preview Document</h4>
          <p>This document format cannot be previewed or there was an error loading it.</p>
          <!-- Download button removed from bottom -->
        </div>
      </div>

      <!-- Footer - only show for images -->
      <div v-if="isImage && !loading && documentDataUrl" class="document-footer">
        <span class="zoom-level">Zoom: {{ Math.round(zoomLevel * 100) }}%</span>
        <span class="document-instructions">Use mouse wheel to zoom • Click and drag to move</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DocumentViewModal",
  props: {
    show: { type: Boolean, default: false },
    documentSrc: { type: String, default: "" },
    documentTitle: { type: String, default: "Document" },
    documentType: { type: String, default: "" }
  },
  emits: ["close"],
  data() {
    return {
      // Document processing
      loading: false,
      documentDataUrl: null,

      // Image zoom and drag properties
      zoomLevel: 1,
      zoomStep: 0.1,
      minZoom: 0.5,
      maxZoom: 3,
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 },
      lastDragOffset: { x: 0, y: 0 }
    };
  },
  computed: {
    isImage() {
      return this.documentType && this.documentType.startsWith('image/');
    },
    isPdf() {
      return this.documentType === 'application/pdf';
    },
    isBase64() {
      return this.documentSrc && (
          this.documentSrc.startsWith('data:') ||
          /^[A-Za-z0-9+/]*={0,2}$/.test(this.documentSrc)
      );
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadDocument();
      } else {
        this.resetModal();
      }
    },
    documentSrc() {
      if (this.show) {
        this.loadDocument();
      }
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    resetModal() {
      this.loading = false;
      this.documentDataUrl = null;
      this.resetZoomAndPosition();
    },

    async loadDocument() {
      if (!this.documentSrc) {
        this.documentDataUrl = null;
        return;
      }

      this.loading = true;
      this.documentDataUrl = null;

      try {
        // If it's already a data URL (starts with data:), use it directly
        if (this.documentSrc.startsWith('data:')) {
          this.documentDataUrl = this.documentSrc;
        }
        // If it's raw base64, convert to data URL
        else if (this.isBase64) {
          this.convertBase64ToDataUrl();
        }
        // Check if it's a URL (original document from API)
        else if (this.documentSrc.startsWith('http')) {
          this.documentDataUrl = this.documentSrc;
        }
        // Handle other cases
        else {
          this.documentDataUrl = this.documentSrc;
        }
      } catch (error) {
        console.error('Error loading document:', error);
        this.documentDataUrl = null;
      } finally {
        this.loading = false;
      }
    },

    convertBase64ToDataUrl() {
      try {
        let mimeType = this.documentType || this.detectMimeTypeFromBase64(this.documentSrc);

        // Create data URL from base64 string
        // Format: data:[mime-type];base64,[base64-string]
        this.documentDataUrl = `data:${mimeType};base64,${this.documentSrc}`;
      } catch (error) {
        console.error('Error converting base64 to data URL:', error);
        this.documentDataUrl = null;
      }
    },

    detectMimeTypeFromBase64(base64String) {
      // Simple MIME type detection based on base64 signature
      if (base64String.startsWith('/9j/') || base64String.startsWith('iVBOR')) {
        return 'image/jpeg';
      } else if (base64String.startsWith('JVBER')) {
        return 'application/pdf';
      } else if (base64String.startsWith('R0lGOD')) {
        return 'image/gif';
      } else if (base64String.startsWith('UklGR')) {
        return 'image/webp';
      } else {
        return 'application/octet-stream';
      }
    },

    // Image zoom methods
    zoomIn() {
      if (this.zoomLevel < this.maxZoom) {
        const oldZoom = this.zoomLevel;
        this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom);
        this.adjustDragOffsetOnZoom(oldZoom, this.zoomLevel);
      }
    },
    zoomOut() {
      if (this.zoomLevel > this.minZoom) {
        const oldZoom = this.zoomLevel;
        this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
        this.adjustDragOffsetOnZoom(oldZoom, this.zoomLevel);

        if (this.zoomLevel === 1) {
          this.resetPosition();
        }
      }
    },
    resetZoom() {
      const oldZoom = this.zoomLevel;
      this.zoomLevel = 1;
      this.adjustDragOffsetOnZoom(oldZoom, this.zoomLevel);
      this.resetPosition();
    },
    resetZoomAndPosition() {
      this.zoomLevel = 1;
      this.resetPosition();
    },
    resetPosition() {
      this.dragOffset = { x: 0, y: 0 };
      this.lastDragOffset = { x: 0, y: 0 };
    },
    handleWheel(event) {
      if (!this.isImage) return;
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    },
    startDrag(event) {
      if (this.isImage && this.zoomLevel > 1) {
        this.isDragging = true;
        this.dragStart = {
          x: event.clientX - this.dragOffset.x,
          y: event.clientY - this.dragOffset.y
        };
      }
    },
    onDrag(event) {
      if (this.isImage && this.isDragging && this.zoomLevel > 1) {
        event.preventDefault();
        this.dragOffset = {
          x: event.clientX - this.dragStart.x,
          y: event.clientY - this.dragStart.y
        };
        this.applyDragBoundaries();
      }
    },
    endDrag() {
      if (this.isImage && this.isDragging) {
        this.isDragging = false;
        this.lastDragOffset = { ...this.dragOffset };
      }
    },
    applyDragBoundaries() {
      const container = this.$refs.documentContainer;
      const wrapper = this.$refs.imageWrapper;

      if (!container || !wrapper) return;

      const containerRect = container.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();

      const maxX = Math.max(0, (wrapperRect.width * this.zoomLevel - containerRect.width) / 2);
      const maxY = Math.max(0, (wrapperRect.height * this.zoomLevel - containerRect.height) / 2);

      this.dragOffset.x = Math.max(-maxX, Math.min(maxX, this.dragOffset.x));
      this.dragOffset.y = Math.max(-maxY, Math.min(maxY, this.dragOffset.y));
    },
    adjustDragOffsetOnZoom(oldZoom, newZoom) {
      if (oldZoom !== newZoom) {
        const scaleChange = newZoom / oldZoom;
        this.dragOffset.x = this.dragOffset.x * scaleChange;
        this.dragOffset.y = this.dragOffset.y * scaleChange;
        this.applyDragBoundaries();
      }
    },

    onImageLoad() {
      // Image loaded successfully
      console.log('Image loaded successfully');
    },

    onImageError() {
      console.error('Failed to load image');
      this.documentDataUrl = null;
    },

    // Document download method
    downloadDocument() {
      if (!this.documentDataUrl) return;

      try {
        let blob;
        let filename = this.documentTitle || 'document';

        if (this.documentDataUrl.startsWith('data:')) {
          // Handle data URLs
          const byteString = atob(this.documentDataUrl.split(',')[1]);
          const mimeType = this.documentDataUrl.split(',')[0].split(':')[1].split(';')[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);

          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }

          blob = new Blob([ab], { type: mimeType });

          // Set appropriate file extension
          if (mimeType === 'application/pdf') {
            filename += '.pdf';
          } else if (mimeType.startsWith('image/')) {
            const extension = mimeType.split('/')[1];
            filename += `.${extension}`;
          }
        } else {
          // Handle regular URLs
          const link = document.createElement('a');
          link.href = this.documentDataUrl;
          link.download = filename;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }

        // Create download link for blob
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading document:', error);

        // Fallback: open in new tab
        if (this.documentDataUrl) {
          window.open(this.documentDataUrl, '_blank');
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.document-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}

.document-modal-content {
  background-color: #1f2937;
  border-radius: 12px;
  width: 90%;
  height: 90%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
}

.document-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #374151;
  color: white;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.document-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.document-control-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.document-close-btn {
  background: rgba(239, 68, 68, 0.8);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-left: 0.5rem;

  &:hover {
    background: rgba(239, 68, 68, 1);
  }
}

.document-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 1rem;
  position: relative;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;

  i {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: #60a5fa;
  }

  p {
    font-size: 1rem;
    color: #d1d5db;
  }
}

/* Image Viewer Styles */
.image-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: grab;
}

.image-wrapper {
  transition: transform 0.1s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-wrapper img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

/* PDF Viewer Styles */
.pdf-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: white;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

/* Unsupported Format Styles */
.unsupported-format {
  text-align: center;
  color: white;
  padding: 2rem;

  i {
    font-size: 3rem;
    color: #fbbf24;
    margin-bottom: 1rem;
  }

  h4 {
    margin: 1rem 0;
    font-size: 1.5rem;
  }

  p {
    margin-bottom: 1.5rem;
    color: #d1d5db;
  }
}

/* Footer - only for images */
.document-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background: #374151;
  color: #d1d5db;
  font-size: 0.875rem;
}

.zoom-level {
  font-weight: 500;
}

.document-instructions {
  font-style: italic;
}

@media (max-width: 768px) {
  .document-modal-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .document-controls {
    align-self: flex-end;
  }

  .document-footer {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
}
</style>